import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn, signUp, signInWithRedirect, resetPassword } from 'aws-amplify/auth';
import { useAuth } from '../hooks/useAuth.js';
import PageMeta from '../components/PageMeta.jsx';
import { useTranslation } from '../i18n/I18nContext.jsx';
import { useLocalizedPath } from '../hooks/useLocalizedPath.js';

// ── Password strength ──────────────────────────────────────────────────────
function getStrength(pwd) {
  if (!pwd) return null;
  let score = 0;
  if (pwd.length >= 8)          score++;
  if (pwd.length >= 12)         score++;
  if (/[A-Z]/.test(pwd))        score++;
  if (/[0-9]/.test(pwd))        score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { level: 1, key: 'strengthWeak',   color: 'var(--color-danger)'  };
  if (score === 2) return { level: 2, key: 'strengthFair',   color: 'var(--color-warning)' };
  if (score === 3) return { level: 3, key: 'strengthGood',   color: '#60a5fa'              };
  return            { level: 4, key: 'strengthStrong', color: 'var(--color-success)' };
}

// ── SVG icons ─────────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 814 1000" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 405.6 0 315.4 0 224.5c0-149.7 97.7-228.8 192.9-228.8 50.4 0 92.5 32.6 124.4 32.6 30.4 0 77.9-34.2 136-34.2 21.8 0 94.7 2.5 143.7 68.7zm-166.2-77.2c-13.8 16.3-36.3 29.1-58.3 29.1-2.6 0-5.1-.3-7.7-.6.3-3.8.6-7.7.6-12.2 0-41.9 21.8-84.4 52.6-111.1 16.6-14.3 44.3-26.8 66.8-28.4 2.6 4.5 3.2 9 3.2 14.3 0 40.6-16.3 82.8-57.2 108.9z"/>
    </svg>
  );
}

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 1rem',
    background: 'var(--color-bg)',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '12px',
    padding: '2rem',
  },
  logo: {
    fontSize: '1.9rem',
    fontWeight: 800,
    color: 'var(--color-primary)',
    textAlign: 'center',
    marginBottom: '0.25rem',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    textAlign: 'center',
    color: 'var(--color-muted)',
    fontSize: '0.9rem',
    marginBottom: '1.75rem',
  },
  tabRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0',
    background: 'var(--color-bg)',
    borderRadius: '8px',
    padding: '3px',
    marginBottom: '1.75rem',
  },
  tab: (active) => ({
    padding: '0.5rem',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s, color 0.15s',
    background: active ? 'var(--color-surface)' : 'transparent',
    color: active ? 'var(--color-text)' : 'var(--color-muted)',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.3)' : 'none',
  }),
  socialBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.6rem',
    width: '100%',
    padding: '0.65rem',
    marginBottom: '0.6rem',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    color: 'var(--color-text)',
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    margin: '1.25rem 0',
    color: 'var(--color-muted)',
    fontSize: '0.8rem',
  },
  line: {
    flex: 1,
    height: '1px',
    background: 'var(--color-border)',
  },
  fieldGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--color-muted)',
    marginBottom: '0.35rem',
  },
  inputWrap: {
    position: 'relative',
  },
  input: (hasError) => ({
    width: '100%',
    padding: '0.65rem 0.85rem',
    background: 'var(--color-bg)',
    border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--color-border)'}`,
    borderRadius: '8px',
    color: 'var(--color-text)',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  }),
  eyeBtn: {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    padding: '0',
    color: 'var(--color-muted)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
  },
  fieldError: {
    fontSize: '0.8rem',
    color: 'var(--color-danger)',
    marginTop: '0.3rem',
  },
  strengthBar: (level) => ({
    height: '4px',
    borderRadius: '2px',
    marginTop: '0.5rem',
    background: 'var(--color-border)',
    overflow: 'hidden',
  }),
  strengthFill: (level, color) => ({
    height: '100%',
    width: `${(level / 4) * 100}%`,
    background: color,
    transition: 'width 0.2s, background 0.2s',
    borderRadius: '2px',
  }),
  strengthLabel: {
    fontSize: '0.75rem',
    marginTop: '0.25rem',
    textAlign: 'right',
  },
  submitBtn: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--color-primary)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
    marginTop: '0.5rem',
  },
  footer: {
    textAlign: 'center',
    marginTop: '1.25rem',
    fontSize: '0.8rem',
    color: 'var(--color-muted)',
  },
  forgotLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'var(--color-primary)',
    cursor: 'pointer',
    fontSize: '0.8rem',
    textDecoration: 'underline',
  },
};

// ── Component ──────────────────────────────────────────────────────────────
export default function LoginPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lp = useLocalizedPath();

  const [mode, setMode] = useState('signin'); // 'signin' | 'register' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const strength = mode === 'register' ? getStrength(password) : null;

  useEffect(() => {
    if (!loading && user) navigate(lp('/dashboard'), { replace: true });
  }, [user, loading, navigate]);

  function clearMessages() {
    setFieldErrors({});
    setServerError('');
    setSuccessMsg('');
  }

  function switchMode(next) {
    clearMessages();
    setPassword('');
    setConfirm('');
    setMode(next);
  }

  // ── Social ────────────────────────────────────────────────────────────────
  function handleSocial(provider) {
    clearMessages();
    signInWithRedirect({ provider });
  }

  // ── Sign in ───────────────────────────────────────────────────────────────
  async function handleSignIn(e) {
    e.preventDefault();
    clearMessages();
    const errs = {};
    if (!email) errs.email = t('auth.errEmailRequired');
    if (!password) errs.password = t('auth.errPasswordRequired');
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }

    setSubmitting(true);
    try {
      await signIn({ username: email.trim(), password });
      navigate(lp('/dashboard'), { replace: true });
    } catch (err) {
      setServerError(friendlyError(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Register ──────────────────────────────────────────────────────────────
  async function handleRegister(e) {
    e.preventDefault();
    clearMessages();
    const errs = {};
    if (!email) errs.email = t('auth.errEmailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = t('auth.errEmailInvalid');
    if (!password) errs.password = t('auth.errPasswordRequired');
    else if (password.length < 8) errs.password = t('auth.errPasswordLength');
    if (password !== confirm) errs.confirm = t('auth.errPasswordConfirm');
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }

    setSubmitting(true);
    try {
      await signUp({
        username: email.trim(),
        password,
        options: { userAttributes: { email: email.trim() } },
      });
      setSuccessMsg(t('auth.successRegister'));
      switchMode('signin');
    } catch (err) {
      setServerError(friendlyError(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Forgot password ───────────────────────────────────────────────────────
  async function handleForgot(e) {
    e.preventDefault();
    clearMessages();
    if (!email) { setFieldErrors({ email: t('auth.errEmailRequired') }); return; }

    setSubmitting(true);
    try {
      await resetPassword({ username: email.trim() });
      setSuccessMsg(t('auth.successForgot'));
    } catch (err) {
      // Federated (Google/Apple) users have no native password — guide them back
      const msg = err?.message ?? String(err);
      if (/user.*not.*found|usernot|cannot.*reset.*password.*external/i.test(msg)) {
        setServerError(t('auth.errForgotFederated'));
      } else {
        setServerError(friendlyError(err, t));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <main id="main-content" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" role="status" aria-label={t('auth.loadingAriaLabel')} />
      </main>
    );
  }

  const isForgot = mode === 'forgot';

  return (
    <div style={S.page}>
      <div style={S.card} role="main">
        {/* Logo */}
        <div style={S.logo} aria-label={t('auth.logo')}>{t('auth.logo')}</div>
        <p style={S.subtitle}>
          {isForgot
            ? t('auth.subForgot')
            : mode === 'register'
              ? t('auth.subRegister')
              : t('auth.subSignIn')}
        </p>

        {/* Mode tabs (only when not in forgot flow) */}
        {!isForgot && (
          <div style={S.tabRow} role="tablist" aria-label={t('auth.tabAriaLabel')}>
            <button
              role="tab"
              aria-selected={mode === 'signin'}
              style={S.tab(mode === 'signin')}
              onClick={() => switchMode('signin')}
            >
              {t('auth.tabSignIn')}
            </button>
            <button
              role="tab"
              aria-selected={mode === 'register'}
              style={S.tab(mode === 'register')}
              onClick={() => switchMode('register')}
            >
              {t('auth.tabRegister')}
            </button>
          </div>
        )}

        {/* Alerts */}
        {serverError && (
          <div className="alert alert-danger" role="alert">{serverError}</div>
        )}
        {successMsg && (
          <div className="alert alert-success" role="status">{successMsg}</div>
        )}

        {/* Social buttons (not shown in forgot mode) */}
        {!isForgot && (
          <>
            <button
              style={S.socialBtn}
              onClick={() => handleSocial('Google')}
              aria-label={t('auth.socialGoogleAriaLabel')}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.background = 'rgba(108,99,255,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.background = 'var(--color-bg)'; }}
            >
              <GoogleIcon />
              {t('auth.socialGoogle')}
            </button>
            <button
              style={S.socialBtn}
              onClick={() => handleSocial('Apple')}
              aria-label={t('auth.socialAppleAriaLabel')}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.background = 'rgba(108,99,255,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.background = 'var(--color-bg)'; }}
            >
              <AppleIcon />
              {t('auth.socialApple')}
            </button>

            <div style={S.divider} aria-hidden="true">
              <span style={S.line} />
              {t('auth.or')}
              <span style={S.line} />
            </div>
          </>
        )}

        {/* Form */}
        <form
          onSubmit={isForgot ? handleForgot : mode === 'register' ? handleRegister : handleSignIn}
          noValidate
          aria-label={isForgot ? t('auth.formAriaForgot') : mode === 'register' ? t('auth.formAriaRegister') : t('auth.formAriaSignIn')}
        >
          {/* Email */}
          <div style={S.fieldGroup}>
            <label htmlFor="auth-email" style={S.label}>{t('auth.emailLabel')}</label>
            <input
              id="auth-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{ ...S.input(!!fieldErrors.email), paddingRight: '0.85rem' }}
              aria-invalid={!!fieldErrors.email}
              aria-describedby={fieldErrors.email ? 'email-error' : undefined}
              placeholder={t('auth.emailPlaceholder')}
            />
            {fieldErrors.email && (
              <div id="email-error" style={S.fieldError} role="alert">{fieldErrors.email}</div>
            )}
          </div>

          {/* Password */}
          {!isForgot && (
            <div style={S.fieldGroup}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.35rem' }}>
                <label htmlFor="auth-password" style={{ ...S.label, marginBottom: 0 }}>{t('auth.passwordLabel')}</label>
                {mode === 'signin' && (
                  <button
                    type="button"
                    style={S.forgotLink}
                    onClick={() => switchMode('forgot')}
                  >
                    {t('auth.forgotLink')}
                  </button>
                )}
              </div>
              <div style={S.inputWrap}>
                <input
                  id="auth-password"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{ ...S.input(!!fieldErrors.password), paddingRight: '2.75rem' }}
                  aria-invalid={!!fieldErrors.password}
                  aria-describedby={
                    [fieldErrors.password ? 'pwd-error' : '', strength ? 'pwd-strength' : '']
                      .filter(Boolean).join(' ') || undefined
                  }
                  placeholder={mode === 'register' ? t('auth.passwordPlaceholder') : ''}
                />
                <button
                  type="button"
                  style={S.eyeBtn}
                  onClick={() => setShowPwd(v => !v)}
                  aria-label={showPwd ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  <EyeIcon open={showPwd} />
                </button>
              </div>
              {fieldErrors.password && (
                <div id="pwd-error" style={S.fieldError} role="alert">{fieldErrors.password}</div>
              )}
              {/* Strength indicator (register only) */}
              {mode === 'register' && password && strength && (
                <div id="pwd-strength" aria-live="polite">
                  <div style={S.strengthBar(strength.level)}>
                    <div style={S.strengthFill(strength.level, strength.color)} />
                  </div>
                  <div style={{ ...S.strengthLabel, color: strength.color }}>
                    {t(`auth.${strength.key}`)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Confirm password (register only) */}
          {mode === 'register' && (
            <div style={S.fieldGroup}>
              <label htmlFor="auth-confirm" style={S.label}>{t('auth.confirmLabel')}</label>
              <div style={S.inputWrap}>
                <input
                  id="auth-confirm"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  style={{ ...S.input(!!fieldErrors.confirm), paddingRight: '2.75rem' }}
                  aria-invalid={!!fieldErrors.confirm}
                  aria-describedby={fieldErrors.confirm ? 'confirm-error' : undefined}
                />
                <button
                  type="button"
                  style={S.eyeBtn}
                  onClick={() => setShowConfirm(v => !v)}
                  aria-label={showConfirm ? t('auth.hideConfirmPassword') : t('auth.showConfirmPassword')}
                >
                  <EyeIcon open={showConfirm} />
                </button>
              </div>
              {fieldErrors.confirm && (
                <div id="confirm-error" style={S.fieldError} role="alert">{fieldErrors.confirm}</div>
              )}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            style={{
              ...S.submitBtn,
              opacity: submitting ? 0.65 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting
              ? t('auth.submitWaiting')
              : isForgot
                ? t('auth.submitForgot')
                : mode === 'register'
                  ? t('auth.submitRegister')
                  : t('auth.submitSignIn')}
          </button>
        </form>

        {/* Footer */}
        <div style={S.footer}>
          {isForgot ? (
            <button type="button" style={S.forgotLink} onClick={() => switchMode('signin')}>
              {t('auth.backToSignIn')}
            </button>
          ) : mode === 'register' ? (
            <p style={{ margin: '0.75rem 0 0' }}>
              {t('auth.termsConsent')}{' '}
              <Link to={lp('/terms')} style={{ color: 'var(--color-primary)' }}>{t('auth.termsLink')}</Link>
              {' '}{t('auth.termsAnd')}{' '}
              <Link to={lp('/privacy')} style={{ color: 'var(--color-primary)' }}>{t('auth.privacyLink')}</Link>.
            </p>
          ) : (
            <p style={{ margin: '0.75rem 0 0' }}>
              <Link to={lp('/privacy')} style={{ color: 'inherit' }}>{t('auth.footerPrivacy')}</Link>
              {' · '}
              <Link to={lp('/terms')} style={{ color: 'inherit' }}>{t('auth.footerTerms')}</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Error message helper ───────────────────────────────────────────────────
function friendlyError(err, t) {
  const msg = err?.message ?? String(err);
  if (/incorrect.*username.*password|notauthorized/i.test(msg)) return t('auth.errIncorrect');
  if (/user.*not.*found|usernot/i.test(msg)) return t('auth.errNotFound');
  if (/username.*exists|already.*registered/i.test(msg)) return t('auth.errExists');
  if (/password.*not.*conform|password.*policy/i.test(msg)) return t('auth.errPasswordPolicy');
  if (/invalid.*email/i.test(msg)) return t('auth.errEmailFormat');
  if (/limit.*exceeded|too.*many/i.test(msg)) return t('auth.errRateLimit');
  return msg || t('auth.errGeneric');
}
